import { useEffect } from "react";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const GIVING_URL = process.env.REACT_APP_GIVING_URL;

export const usePageMeta = (title, description) => {
  useEffect(() => {
    document.title = title;
    const meta = document.querySelector('meta[name="description"]');
    if (meta && description) meta.setAttribute("content", description);
  }, [title, description]);
};

export const IMAGES = {
  hero: "/images/hero.jpg",
  feeding: "/images/feeding.jpg",
  feedingCard: "/images/feeding-card.jpg",
  dream: "/images/dream.jpg",
  dreamCard: "/images/dream-card.jpg",
  stories: "/images/stories.jpg",
  about: "/images/about.jpg",
};

export const PROGRAMS = [
  {
    slug: "feeding-centers",
    number: "01",
    name: "Feeding Centers",
    tagline: "A hot meal, every weekend.",
    intro:
      "Each year we serve thousands of meals to children who would otherwise go hungry. Twenty-seven feeding centers across the Philippines and Lithuania anchor their neighborhoods — places where a child is always known by name and never turned away.",
    body: "A hungry child cannot learn, play, or dream. So we start with the table. Every center is run by local leaders who cook, serve, and sit down with the same children day after day — because a meal is never just a meal. It is proof that someone stayed.",
    impactsTitle: "What your gift does",
    impacts: [
      { amount: "$25", note: "covers a month of school meals for one child" },
      { amount: "$60", note: "stocks a week of groceries for a family of four" },
      { amount: "$250", note: "keeps a neighborhood center cooking for a week" },
    ],
    stats: [
      { value: "27", label: "centers serving daily" },
      { value: "2", label: "countries" },
      { value: "10K+", label: "meals each year" },
    ],
    image: IMAGES.feeding,
    cardImage: IMAGES.feedingCard,
    alt: "The Hope Center Alabat feeding program in session, children gathered around a volunteer",
    cardAlt: "Children sharing a hot meal together at a Hope Centers feeding center",
  },
  {
    slug: "dream-release",
    number: "02",
    name: "Dream Release",
    tagline: "Education that unlocks a future.",
    intro:
      "Dream Release empowers today's underprivileged children to become tomorrow's leaders. We make education, mentorship, and spiritual discipleship available to poor children and their families — and we walk with them for years, not weeks.",
    body: "A meal changes a day. An education changes a life. Dream Release covers school fees, supplies, and uniforms, then surrounds each student with mentors who believe in them before they believe in themselves.",
    impactsTitle: "Sponsor a child's education",
    impacts: [
      { amount: "$150/yr", note: "sponsors an elementary student" },
      { amount: "$250/yr", note: "sponsors a high school student" },
      { amount: "$350/yr", note: "sponsors a college student" },
    ],
    stats: [
      { value: "27", label: "students enrolled" },
      { value: "12", label: "years running" },
      { value: "92%", label: "stay in school" },
    ],
    image: IMAGES.dream,
    cardImage: IMAGES.dream,
    alt: "Dream Release scholars in blue shirts in front of the HCDR Center",
    cardAlt: "Dream Release scholars in program shirts at the HCDR Center",
  },
];

export const STATS = [
  { value: "27", label: "Feeding centers across two countries" },
  { value: "10K+", label: "Meals served every year" },
  { value: "27", label: "Students in Dream Release" },
  { value: "100%", label: "Community-led programs" },
];

export const CHAPTERS = [
  {
    number: "01",
    title: "We feed.",
    text: "Everything starts at the table. A child who eats today can learn tomorrow — so our feeding centers serve a hot meal every weekend, cooked and shared by neighbors.",
  },
  {
    number: "02",
    title: "We teach.",
    text: "Through Dream Release, school fees, supplies, mentors, and discipleship turn a full stomach into a full life. We back each child's dream until it stands on its own.",
  },
  {
    number: "03",
    title: "We stay.",
    text: "We don't drop in and disappear. Our centers are rooted in the community, led by local leaders, and committed to the same families for the long haul.",
  },
];

export const STORIES = [
  {
    quote:
      "Before the feeding center opened, my children went to school hungry. Now they run there every morning — for the food, yes, but mostly for the people.",
    name: "Marites D.",
    role: "Parent, Cebu",
    featured: true,
  },
  {
    quote:
      "Dream Release paid for my books when no one else could. This year I became the first in my family to finish high school.",
    name: "Janelle R.",
    role: "Dream Release graduate",
  },
  {
    quote:
      "I've volunteered for six years. What keeps me coming back is that local leaders run everything — we just show up and help.",
    name: "Tom W.",
    role: "Volunteer, Las Cruces",
  },
];

export const VALUES = [
  {
    title: "Dignity first",
    text: "Every child is a story, not a statistic. We serve with honor, never pity.",
  },
  {
    title: "Local leadership",
    text: "Programs are run by the community, for the community. We equip, they lead.",
  },
  {
    title: "Known by name",
    text: "Children join our programs and stay in them — every child is enrolled, known, and walked with for the long haul.",
  },
  {
    title: "Faith in action",
    text: "Our hope is hands-on — cooked, carried, tutored, and shown up for.",
  },
];

export const TEAM_LEADERS = [
  { name: "Sue Yeager", role: "Director", photo: "/team/sue-yeager.jpg" },
  { name: "Lucio & Desi Luttrell", role: "Outreach Coordinators", photo: "/team/lucio-desi-luttrell.jpg" },
  { name: "Ferdinand Onandia", role: "Creative Director", photo: "/team/ferdinand-onandia.jpg" },
  { name: "Pastor Analisa Satrain", role: "International Coordinator", photo: "/team/analisa-satrain.jpg" },
];

export const TEAM_COORDINATORS = [
  { name: "Pastor Joedy Lim", role: "Dagupan, Pangasinan", photo: "/team/joedy-lim.jpg" },
  { name: "Arcel Balinquit", role: "Tuazon, Butuan", photo: "/team/arcel-balinquit.jpg" },
  { name: 'Pastor Isidro "Edu" Patriarca', role: "Candelaria, Quezon", photo: "/team/edu-patriarca.jpg" },
  { name: "Mary Anne Satrain", role: "Caridad, Cavite", photo: "/team/mary-anne-satrain.jpg" },
  { name: "Pastor Mode Lucena", role: "Dalahican, Quezon", photo: "/team/mode-lucena.jpg" },
  { name: "Pastor Eddie Rosales", role: "Catanauan, Quezon", photo: "/team/eddie-rosales.jpg" },
  { name: "Pastor Leonila Catangay", role: "Candelaria, Quezon", photo: "/team/leonila-catangay.jpg" },
  { name: "Ramunas & Vilma Arka", role: "Panevezys, Lithuania", photo: "/team/arka.jpg" },
  { name: "Izaokas & Sigita Jukna", role: "Panevezys, Lithuania", photo: "/team/jukna.jpg" },
  { name: "Kenji & Masami Mase", role: "Nagoya, Japan", photo: "/team/mase.jpg" },
];

export const DONATE_AMOUNTS = [25, 50, 120, 250];

export const AMOUNT_NOTES = {
  25: "A month of school meals for one child",
  50: "Two weeks of groceries for a family",
  120: "A semester of supplies for a student",
  250: "Stocks a feeding center for a week",
};

export const DESIGNATIONS = [
  { slug: "most-needed", label: "Where it's needed most" },
  { slug: "feeding-centers", label: "Feeding Centers" },
  { slug: "dream-release", label: "Dream Release" },
];

export const DREAM_TIERS = [
  { label: "Elementary", amount: 150, note: "A full year of elementary education for one student" },
  { label: "High School", amount: 250, note: "A full year of high school for one student" },
  { label: "College", amount: 350, note: "A full year of college for one student" },
];

export const MARQUEE_ITEMS = ["Nourish", "Educate", "Empower", "Belong", "Hope"];
